import React, { useState, useEffect } from "react";
import { DualHeader } from "@/components/DualHeader";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Tent, MapPin, CircleDollarSign, Calendar, Grid2X2, Map, ArrowLeft, AlertTriangle, Bird, Flame, Trash2, Bug, Dog, Phone, Clock, Shield, Truck } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem, ToggleGroupCustom } from "@/components/ui/toggle-group";
import CampSiteGrid from "@/components/camping/CampSiteGrid";
import CampSiteMap from "@/components/camping/CampSiteMap";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useLocation } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import GeneralCampingForm from "@/components/camping/components/GeneralCampingForm";
import { CartItem } from "@/contexts/CartContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { EmergencyInfo } from "@/components/fishing-regulations/sections/EmergencyInfo";
import { ClosureNotice } from "@/components/ClosureNotice";
import { CLOSURES, CLOSURE_MESSAGE_RV, CLOSURE_MESSAGE_GENERAL } from "@/config/closures";

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4";

const CampingPermits = () => {
  const [campingSite, setCampingSite] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  // Default to the first tab that isn't closed
  const defaultCampingType = !CLOSURES.rvSites
    ? "rv-sites"
    : !CLOSURES.wildHorse
      ? "wild-horse"
      : "general-camping";
  const [campingType, setCampingType] = useState(defaultCampingType);
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [heroImageError, setHeroImageError] = useState(false);
  const [activeTab, setActiveTab] = useState<"permits" | "rules">("permits");
  const [showGeneralCampingForm, setShowGeneralCampingForm] = useState(false);
  const [editData, setEditData] = useState<CartItem | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle edit data from location state
  useEffect(() => {
    if (location.state?.editItem) {
      const item = location.state.editItem;
      setEditData(item);

      // Handle different types of camping items
      if (item.type === "general-camping") {
        setShowGeneralCampingForm(true);
      } else if ((item.type === "camping" || item.type === "rv-booking") && item.siteId) {
        // For specific camping sites, navigate to the booking page
        const siteType = (item.type === "rv-booking" || item.campType === "rv") ? "rv" : "camping";
        navigate(`/booking/${siteType}/${item.siteId}`, {
          state: {
            editItem: item
          }
        });
      }

      // Clear location state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, navigate]);
  return <div className="min-h-screen bg-tribal-light">
      <DualHeader />
      
      {/* Hero Section */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center px-6">
        <div className="absolute top-4 left-4 z-20">
          <Button variant="outline" size="sm" className="bg-white/80 hover:bg-white" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('${heroImageError ? FALLBACK_IMAGE : '/lovable-uploads/75c303b8-fe5d-46cb-a63a-f0c6465118a8.png'}')`,
        backgroundPosition: "center 35%"
      }}>
          <img src={heroImageError ? FALLBACK_IMAGE : '/lovable-uploads/75c303b8-fe5d-46cb-a63a-f0c6465118a8.png'} className="hidden" onError={() => setHeroImageError(true)} alt="Hero background" />
        </div>
        
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 w-full">
          <div className="text-center mb-8 animate-fade-up">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-shadow-lg">Camping Permits</h1>
            <p className="text-lg text-white max-w-2xl mx-auto text-shadow-md">
              Experience the beauty of tribal lands with camping permits for the Shoshone-Paiute Tribes
            </p>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Main Tabs: Purchase Permits | Rules & Regulations */}
          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as "permits" | "rules")} className="w-full mb-8">
            <TabsList className="w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="permits" className="text-sm md:text-base">
                Purchase Permits
              </TabsTrigger>
              <TabsTrigger value="rules" className="text-sm md:text-base">
                Rules & Regulations
              </TabsTrigger>
            </TabsList>
            
            {/* Purchase Permits Tab Content */}
            <TabsContent value="permits">
              <div className="mb-8">
                {(CLOSURES.rvSites || CLOSURES.generalCamping) && (
                  <ClosureNotice className="mb-6" message={CLOSURE_MESSAGE_RV} />
                )}
                <Tabs value={campingType} onValueChange={setCampingType} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-6 gap-1 p-1.5">
                    <TabsTrigger value="rv-sites" disabled={CLOSURES.rvSites} className="text-sm md:text-base">
                      <Tent className="mr-2 h-4 w-4" />
                      {isMobile ? <div className="flex flex-col items-center leading-tight">
                          <span>RV Sites</span>
                          {CLOSURES.rvSites && <span className="text-[10px] font-semibold uppercase text-destructive">Closed</span>}
                        </div> : <span className="flex items-center gap-2">
                          RV Sites
                          {CLOSURES.rvSites && <span className="text-[10px] font-semibold uppercase text-destructive">Closed</span>}
                        </span>}
                    </TabsTrigger>
                    <TabsTrigger value="general-camping" disabled={CLOSURES.generalCamping} className="text-sm md:text-base">
                      {isMobile ? <div className="flex flex-col items-center leading-tight">
                          <span>General</span>
                          <span>Camping</span>
                          {CLOSURES.generalCamping && <span className="text-[10px] font-semibold uppercase text-destructive">Closed</span>}
                        </div> : <span className="flex items-center gap-2">
                          General Camping
                          {CLOSURES.generalCamping && <span className="text-[10px] font-semibold uppercase text-destructive">Closed</span>}
                        </span>}
                    </TabsTrigger>
                    <TabsTrigger value="wild-horse" disabled={CLOSURES.wildHorse} className="text-sm md:text-base">
                      {isMobile ? <div className="flex flex-col items-center leading-tight">
                          <span>Wild Horse</span>
                          <span>Camp Sites</span>
                        </div> : "Wild Horse Camp Sites"}
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                      <h3 className="text-xl font-semibold">
                        {campingType === "rv-sites" ? "RV Sites with Electrical Hookups" : campingType === "general-camping" ? "General Camping (Non-Electrical)" : "Wild Horse Numbered Sites"}
                      </h3>
                      <p className="text-sm text-tribal-neutral">
                        {campingType === "rv-sites" ? "Sites at Mountain View & Sheep Creek with electrical hookups" : campingType === "general-camping" ? "Open camping areas without specific site numbers" : "Numbered sites at Wild Horse Reservoir"}
                      </p>
                    </div>
                    
                    <div className="flex justify-end">
                      <ToggleGroupCustom type="single" value={viewMode} onValueChange={value => value && setViewMode(value as "grid" | "map")} className="border border-tribal-neutral/20 rounded-md" layout="row">
                        <ToggleGroupItem value="grid" aria-label="Grid view" className="flex items-center gap-2">
                          <Grid2X2 className="h-4 w-4" />
                          <span className="text-sm">Grid</span>
                        </ToggleGroupItem>
                        <ToggleGroupItem value="map" aria-label="Map view" className="flex items-center gap-2">
                          <Map className="h-4 w-4" />
                          <span className="text-sm">Map</span>
                        </ToggleGroupItem>
                      </ToggleGroupCustom>
                    </div>
                  </div>
                  
                  <TabsContent value="rv-sites">
                    {CLOSURES.rvSites ? <ClosureNotice message={CLOSURE_MESSAGE_RV} /> : <>
                    {viewMode === "grid" && <div className="mb-4">
                        <Tabs value={locationFilter} onValueChange={setLocationFilter} className="w-full">
                          <TabsList className="w-full mb-4 grid-cols-[80px_1fr_1fr]">
                            <TabsTrigger value="all" className="flex-initial min-w-0">All</TabsTrigger>
                            <TabsTrigger value="mountain-view" className="flex-1">
                              <MapPin className="h-3.5 w-3.5 mr-1" />
                              Mountain View
                            </TabsTrigger>
                            <TabsTrigger value="sheep-creek" className="flex-1">
                              <MapPin className="h-3.5 w-3.5 mr-1" />
                              Sheep Creek
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>}
                    
                    {viewMode === "grid" ? <CampSiteGrid siteType="rv" locationFilter={locationFilter} /> : <Card className="p-4">
                        <CampSiteMap siteType="rv" />
                      </Card>}
                    </>}
                  </TabsContent>
                  
                  <TabsContent value="general-camping">
                    {CLOSURES.generalCamping ? <ClosureNotice message={CLOSURE_MESSAGE_GENERAL} /> : <Card>
                      <CardContent className="pt-6">
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-semibold mb-2">General Camping Information</h3>
                          <p className="text-tribal-neutral">General camping does not have assigned sites and is available at all reservoir areas</p>
                          
                          <p className="text-sm text-tribal-neutral mt-3 italic">
                            If you haven't yet purchased your fishing permit, you can conveniently add General Camping when purchasing your fishing permit—this is the preferred method.
                          </p>
                        </div>
                        
                        <div className="mb-6">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Pricing</TableHead>
                                <TableHead>Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium">$15.00 per night</TableCell>
                                <TableCell>Standard rate for general camping</TableCell>
                              </TableRow>
                              <TableRow>
                                
                                <TableCell>
                              </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                        
                        <div className="bg-tribal-primary/5 p-4 rounded-md mb-6">
                          <h4 className="font-medium mb-2 flex items-center">
                            <Tent className="h-4 w-4 mr-2 text-tribal-primary" />
                            General Camping Features
                          </h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Fire rings available (when no fire restrictions in place)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Access to communal water sources</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Vault toilets located throughout camping areas</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>No electrical hookups available</span>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row justify-center gap-4 px-4">
                          <Button className="flex-1 bg-tribal-primary hover:bg-tribal-primary/90 text-white" onClick={() => navigate('/permits/fishing')}>
                            Purchase Fishing Permit
                          </Button>
                          <Button className="flex-1 bg-tribal-primary hover:bg-tribal-primary/90 text-white" onClick={() => setShowGeneralCampingForm(true)}>
                            Book General Camping
                          </Button>
                        </div>
                      </CardContent>
                    </Card>}
                  </TabsContent>
                  
                  <TabsContent value="wild-horse">
                    {viewMode === "grid" && <div className="mb-4">
                        <div className="flex items-center space-x-4">
                          <label htmlFor="wild-horse-location" className="text-sm font-medium w-24">
                            Filter by:
                          </label>
                          <Select value={locationFilter} onValueChange={setLocationFilter}>
                            <SelectTrigger id="wild-horse-location" className="w-[240px]">
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Sites</SelectItem>
                              <SelectItem value="north">North Shore (Sites 1-10)</SelectItem>
                              <SelectItem value="south">South Shore (Sites 11-20)</SelectItem>
                              <SelectItem value="east">East Shore (Sites 21-26)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>}
                    
                    {viewMode === "grid" ? <CampSiteGrid siteType="wild-horse" locationFilter={locationFilter} /> : <Card className="p-4">
                        <div className="mb-4">
                          
                        </div>
                        <CampSiteMap siteType="wild-horse" locationFilter={locationFilter} />
                      </Card>}
                  </TabsContent>
                </Tabs>
              </div>
              
              <Card className="mb-8">
                <CardHeader>
                  <h2 className="text-xl font-bold">Camping Rules & Guidelines</h2>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex gap-2">
                      <span className="text-tribal-primary">•</span>
                      <span>Maximum stay is 14 consecutive days at any one location</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-tribal-primary">•</span>
                      <span>Quiet hours are from 10:00 PM to 7:00 AM</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-tribal-primary">•</span>
                      <span>Pack out all trash and leave no trace</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-tribal-primary">•</span>
                      <span>Campfires allowed only in designated fire rings when no fire restrictions are in place</span>
                    </li>
                    
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Rules & Regulations Tab Content */}
            <TabsContent value="rules">
              <Card className="mb-8">
                <CardHeader className="text-center">
                  <h2 className="text-2xl font-bold">Camping Rules and Regulations</h2>
                  <div className="text-tribal-secondary text-sm font-medium flex flex-col items-center justify-center mt-2">
                    <div className="text-center font-semibold mb-2">Fish & Game Department:</div>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Phone className="h-4 w-4 text-tribal-primary" />
                        <span>208-759-3246</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-tribal-primary" />
                        <span>Monday – Friday</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-tribal-primary" />
                        <span>8:00 am – 5:00 pm (MST)</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-8">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="general-camping-policy">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Tent className="h-5 w-5 text-tribal-primary" />
                            General Camping Policy
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <div className="bg-tribal-primary/5 p-5 rounded-lg">
                            <h4 className="font-medium mb-3">Daily General Camping Permit</h4>
                            <p className="text-sm text-tribal-neutral mb-4">
                              Most campsites are non-electrical and require a Daily General Camping Permit. Fees are charged per camp vehicle, not per campsite. General campsites at Mountain View, Sheep Creek, and Lake Billy Shaw reservoirs are not numbered, allowing general camping in any suitable location around these reservoirs.
                            </p>
                            <p className="text-sm text-tribal-neutral">
                              There are 18 identified campsites at Mountain View Reservoir that have electrical power pedestals. Only one camping vehicle is allowed per site.
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="quiet-hours">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-tribal-primary" />
                            Quiet Hours
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Quiet hours are in effect from 10pm-7am.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Please be respectful of other campers during these hours.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Loud music, voices, and other disruptive noises should be kept to a minimum.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Generators should not be operated during quiet hours.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="legal-safety-acknowledgment">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-tribal-primary" />
                            Legal and Safety Acknowledgment
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
                            <p className="text-sm text-tribal-neutral font-medium">
                              "BY EXECUTION OF A TRIBAL HUNTING, FISHING, TRAPPING, AND CAMPING PERMIT THE APPLICANT 
                              ACKNOWLEDGES AND VOLUNTARILY CONSENTS TO CIVIL OR CRIMINAL ADJUDICATION IN THE SHOSHONE-PAIUTE 
                              TRIBAL COURT, FOR ANY INFRACTIONS OF THE CURRENT DUCK VALLEY HUNTING AND FISHING ORDINANCE, 
                              AND EXISTING FEDERAL STATUTES."
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="wildlife-environment">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Bird className="h-5 w-5 text-tribal-primary" />
                            Wildlife & Environment Guidelines
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Enjoy bird watching around the lakes. Stay on main roads.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Respect tribal sovereignty and land.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="campfire-safety">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Flame className="h-5 w-5 text-tribal-primary" />
                            Campfire Safety
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Never leave fires unattended. Fully extinguish when not supervised.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Observe local Fire Department burn bans and restrictions.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Fires must be built only within designated fire rings or grills.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="trash-cleanliness">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-tribal-primary" />
                            Trash & Cleanliness
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Maintain the beauty of Duck Valley; dispose of trash responsibly.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>No littering allowed.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Do not dispose of fish remains in dumpsters.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Restroom facilities are provided for your convenience; please help us keep them clean.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="firearms">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-red-600" />
                            Firearms
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Discharging firearms is strictly prohibited.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="atv-rules">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Truck className="h-5 w-5 text-amber-600" />
                            ATV & UTV Rules
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>When operating ATVs or UTVs, please stay on maintained roads to avoid damaging the land, vegetation, or disturbing wildlife.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="wildlife-precautions">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Bug className="h-5 w-5 text-tribal-primary" />
                            Wildlife Precautions
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Snakes are common (rattlesnakes, gopher snakes, racers, garter snakes). Stay alert.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Give snakes plenty of room; back away slowly if encountered.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Ticks: Regularly check yourself, children, and pets after being outdoors.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Mosquitoes: Use insect repellent and wear long sleeves and pants, especially at dusk and dawn.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="pets-policy">
                        <AccordionTrigger className="text-lg font-medium">
                          <div className="flex items-center gap-2">
                            <Dog className="h-5 w-5 text-tribal-primary" />
                            Pets Policy
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm px-4">
                          <ul className="space-y-2">
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Pets must be on leashes at all times.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-tribal-primary">•</span>
                              <span>Clean up after your pets immediately.</span>
                            </li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                      <h4 className="font-medium mb-2">User Acknowledgement Statement</h4>
                      <p className="text-sm text-tribal-neutral">
                        Users acknowledge these regulations upon purchase of any camping permit.
                      </p>
                    </div>
                    
                    {/* Emergency Information - Displayed permanently at bottom */}
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-5">
                      <EmergencyInfo standalone={true} redText={true} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      {/* General Camping Form Dialog */}
      <Dialog open={showGeneralCampingForm} onOpenChange={open => {
      setShowGeneralCampingForm(open);
      if (!open) setEditData(null); // Clear edit data when closing the dialog
    }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <GeneralCampingForm onCancel={() => setShowGeneralCampingForm(false)} editData={editData} />
        </DialogContent>
      </Dialog>
    </div>;
};

export default CampingPermits;
